/**
 * Compound Comparison Shortcut — "compare compound 1 to compound 3"
 *
 * Side-by-side numerical comparison of two compounds using anonymized
 * data (energies, properties, scores). No compound identity exposed.
 *
 * @module gibbs/knowledge/compoundComparisonShortcut
 */

// Keep TIGHT — canonical phrases only.
const COMPARISON_PHRASES = [
    'compare compound',
    'compare compounds',
    'compare the compounds',
    'side by side',
];

/**
 * @param {string} userMessage
 * @returns {boolean}
 */
export function matchComparisonIntent(userMessage) {
    if (!userMessage || typeof userMessage !== 'string') return false;
    const lower = userMessage.toLowerCase();
    return COMPARISON_PHRASES.some((p) => lower.includes(p));
}

/**
 * Parse compound indices from a comparison request.
 * "compare compound 1 to compound 3" → [1, 3]
 * "compare compounds 2 and 5" → [2, 5]
 *
 * @param {string} userMessage
 * @returns {number[]} Array of 1-based compound indices (may be empty)
 */
export function parseComparisonIndices(userMessage) {
    const numbers = [];
    const matches = userMessage.match(/\d+/g);
    if (matches) {
        for (const m of matches) {
            const n = parseInt(m, 10);
            if (n > 0 && n < 1000) numbers.push(n);
        }
    }
    return numbers;
}

/**
 * Format a numeric value for display.
 */
function fmt(val, decimals = 1) {
    if (val == null) return '—';
    return val.toFixed(decimals);
}

/**
 * Build a side-by-side comparison of two compounds.
 *
 * @param {Object} compA - From getCompoundData()[idxA]
 * @param {Object} compB - From getCompoundData()[idxB]
 * @returns {string} Formatted comparison text
 */
export function buildComparison(compA, compB) {
    if (!compA || !compB) {
        return 'Could not find both compounds for comparison.';
    }

    const lines = [];
    lines.push(
        `**Compound ${compA.id} vs Compound ${compB.id}:**\n`
    );

    // Energies
    const eA = compA.energies || {};
    const eB = compB.energies || {};
    const hasEnergies = Object.keys(eA).length > 0
        || Object.keys(eB).length > 0;

    if (hasEnergies) {
        lines.push('**Energies:**');
        const energyKeys = [
            ['interactionScore', 'Interaction Score'],
            ['vdW', 'van der Waals'],
            ['electrostatics', 'Electrostatics'],
            ['hbonds', 'H-bonds'],
            ['solvation', 'Solvation (ddGs)'],
            ['stress', 'Stress'],
            ['gninaAffinity', 'GNINA Affinity'],
        ];
        for (const [key, label] of energyKeys) {
            const a = eA[key];
            const b = eB[key];
            if (a != null || b != null) {
                let better = '';
                if (a != null && b != null) {
                    if (a < b) better = '<<<';
                    else if (a > b) better = '>>>';
                    else better = '===';
                }
                lines.push(
                    `  ${label}: ${fmt(a)} ${better} ${fmt(b)}`
                );
            }
        }
        lines.push('');
    }

    // Properties
    const pA = compA.properties || {};
    const pB = compB.properties || {};
    const hasProps = Object.keys(pA).length > 0
        || Object.keys(pB).length > 0;

    if (hasProps) {
        lines.push('**Properties:**');
        const propKeys = [
            ['MW', 'Mol. Weight', 0],
            ['logP', 'logP', 1],
            ['PSA', 'PSA', 0],
            ['HBA', 'HB Acceptors', 0],
            ['HBD', 'HB Donors', 0],
            ['rotatableBonds', 'Rot. Bonds', 0],
            ['heavyAtoms', 'Heavy Atoms', 0],
        ];
        for (const [key, label, dec] of propKeys) {
            const a = pA[key];
            const b = pB[key];
            if (a != null || b != null) {
                lines.push(
                    `  ${label}: ${fmt(a, dec)} vs ${fmt(b, dec)}`
                );
            }
        }
        lines.push('');
    }

    if (!hasEnergies && !hasProps) {
        lines.push(
            'No energies or properties calculated for these '
            + 'compounds. Run **"dock and score"** first.'
        );
    }

    // Summary
    const scoreA = eA.interactionScore;
    const scoreB = eB.interactionScore;
    if (scoreA != null && scoreB != null) {
        const winner = scoreA < scoreB ? compA.id : compB.id;
        const diff = Math.abs(scoreA - scoreB).toFixed(1);
        lines.push(
            `**Compound ${winner} scores better** by `
            + `${diff} kcal/mol.`
        );
    }

    return lines.join('\n');
}
